import { useNavigation, useNavigationState, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../store';
import { profileRequest } from '../../../store/auth/authActions';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { missionsModalOpened } from '../../../store/missions/missionsActions';
import { selectMissionsModalOpen } from '../../../store/missions/missionsReducer';
import { navigateToTab } from '../../../utils/helpers/navigationService';
import { useBonusCountdown } from '../../../utils/hooks/useBonusCountdown';
import { useDevice } from '../../../utils/hooks/useDevice';
import { RootStackParamList } from '../../navigator';
import { getBackgroundBaseRoute } from '../../Pages/backgroundRouteUtils';
import TopJackpotBanner from '../../shell/header/TopJackpotBanner';
import DesktopDock from '../Desktop/DesktopDock';
import BonusGamesModal from '../Modals/BonusGamesComponents/BonusGamesModal';
import FooterNavButtonsV2, { getNavPlateWidth } from './Nav/FooterNavButtonsV2';
import TournamentDesktopBar from './Tournament/TournamentDesktopBar';
import TournamentMobileNav from './Tournament/TournamentMobileNav';
import TournamentStatsModal from './Tournament/TournamentStatsModal';

type Nav = NativeStackNavigationProp<RootStackParamList>;

type JackpotMode = 'pinned' | 'content' | 'hidden';

// Most routes keep the jackpot fixed above the nav. Content routes render it as part of their
// own scrollable stack; hidden routes have no jackpot treatment at all.
const JACKPOT_MODE_BY_ROUTE: Record<string, JackpotMode> = {
	Friends: 'hidden',
	Event: 'hidden',
	Duels: 'hidden',
	DuelSeason: 'hidden',
	DuelHistory: 'hidden',
	DuelResult: 'hidden',
	DuelScoreBreakdown: 'hidden',
	DuelReward: 'hidden',
	DuelRedemptionHistory: 'hidden',
	Ranking: 'hidden',
	Bonuses: 'content',
	VipActiveMember: 'hidden',
	VipRooms: 'hidden',
};

// BONUSES has two implementations while the redesign is compared against what ships: the
// `BonusesScreen` route drawn from Figma "VIP / bonuses / arcade games" (3618:68740), and the
// `BonusGamesModal` it replaces, which is still mounted below. Flip this to false to send the
// footer button back at the modal.
const USE_BONUSES_SCREEN = true;

/** The redesigned footer is drawn at its own size; kept stable so memoized styles hold. */
const identityScale = (value: number) => value;

const AppFooter = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const loaded = useSelector((s: RootState) => s.bonus.loaded);
	const isLogged = useSelector(selectIsLogged);
	const requestedRef = useRef(false);
	useEffect(() => {
		if (requestedRef.current) return;
		if (!isLogged || loaded) return;
		requestedRef.current = true;
		dispatch(profileRequest());
	}, [isLogged, loaded, dispatch]);

	const { isDesktop, isMobile, safeAreaWidth, safeAreaHeight } = useDevice();
	const scaleSize = identityScale;
	const width = safeAreaWidth;
	const [isBonusGamesModalOpen, setIsBonusGamesModalOpen] = useState(false);
	const isDailyMissionsOpen = useSelector(selectMissionsModalOpen);
	const [isTournamentStatsOpen, setIsTournamentStatsOpen] = useState(false);
	const navigation = useNavigation<Nav>();
	const { navigate } = navigation;
	const collectedAt = useSelector((s: RootState) => s.auth.user?.collectedAt) as string | undefined;

	const bonusHours = useSelector((s: RootState) => s.auth.user?.bonusHours) as string | undefined;

	const { ready } = useBonusCountdown(collectedAt, Number(bonusHours));

	const backgroundRouteName = useNavigationState(state => getBackgroundBaseRoute(state));
	const activeRouteName = useNavigationState(state => {
		const activeRoute = state.routes[state.index ?? state.routes.length - 1];
		return activeRoute?.name;
	});
	// The screen this footer is drawn on. Every screen with a footer brings its own MainLayout, so
	// the jackpot is decided per screen: keyed off the top of the stack instead, the footer of the
	// screen being left switched its jackpot the moment a tab was tapped - while still on screen,
	// under the next one fading in - and the outgoing screen grew one while it faded out.
	const hostRouteName = useRoute().name;

	const isTournaments =
		backgroundRouteName === 'Tournaments' || backgroundRouteName === 'Tournament';
	// The tournaments list is designed without the My tables / Tournaments strip (Figma
	// 2747:16894). The detail screen keeps it - it is the only way into the stats modal - and so
	// does the desktop layout, which that design does not cover.
	const showTournamentMobileNav = isTournaments && backgroundRouteName !== 'Tournaments';
	// The mobile lobby and tournaments list go without the jackpot banner, so their content fits
	// the screen without scrolling.
	const jackpotMode = JACKPOT_MODE_BY_ROUTE[hostRouteName] ?? 'pinned';
	const showJackpot =
		jackpotMode === 'pinned' &&
		hostRouteName !== 'Ranking' &&
		hostRouteName !== 'VipSubscription' &&
		hostRouteName !== 'VipActiveMember' &&
		!((hostRouteName === 'Lobby' || hostRouteName === 'Tournaments') && isMobile);
	const mobileJackpotHeight = showJackpot ? Math.max(64, Math.min(78, width * 0.15)) : 0;
	const mobileFooterHeight =
		scaleSize(showTournamentMobileNav ? width * 0.37 : width * 0.23) + mobileJackpotHeight;
	const tournamentFooterSpacer = Math.min(scaleSize(120), Math.max(scaleSize(40), width * 0.1));
	const tournamentFooterFontSize = scaleSize(width < 700 ? 14 : width < 900 ? 16 : 18);
	// The footer has to leave the same clearance under the bar that the design does, so it needs
	// the width the nav actually drew its plate at.
	const navPlateWidth = getNavPlateWidth(width, safeAreaHeight, isMobile);
	// Memoized: StyleSheet.create on every render allocated new style objects and
	// defeated RN's style-ID caching.
	const styles = useMemo(
		() => createStyles(scaleSize, width, mobileFooterHeight, navPlateWidth),
		[scaleSize, width, mobileFooterHeight, navPlateWidth],
	);
	const activeFooterItem =
		isBonusGamesModalOpen || activeRouteName === 'Bonuses'
			? 'bonuses'
			: isDailyMissionsOpen
				? 'missions'
				: activeRouteName === 'Ranking'
					? 'rankings'
					: activeRouteName === 'BuyPackages' || activeRouteName === 'VipSubscription'
						? 'store'
						: 'home';

	const handleMyTablesPress = () => {
		if (isLogged) {
			navigate('MyTables');
		} else {
			navigate('Login');
		}
	};

	const handleTournamentStatsPress = () => {
		setIsTournamentStatsOpen(true);
	};

	const handleRankingPress = () => {
		navigateToTab(navigation, 'Ranking');
	};

	const handleHomePress = () => {
		navigateToTab(navigation, 'Main');
	};

	const handleBonusGamesPress = () => {
		if (USE_BONUSES_SCREEN) {
			navigateToTab(navigation, 'Bonuses');
			return;
		}
		setIsBonusGamesModalOpen(true);
	};

	const handleStorePress = () => {
		navigate('BuyPackages', { activeTab: 'chips' });
	};

	const handleMissionsPress = () => {
		if (!isLogged) {
			navigate('Login');
			return;
		}

		dispatch(missionsModalOpened());
	};

	// The branch used to key off isXs, which left 600-767 on the desktop footer - a width it
	// does not fit, with the nav items spreading wider than the plate they stand on. isMobile
	// covers the whole phone / small-tablet range instead.
	return (
		<View style={[styles.bottomRoot, isMobile && styles.bottomRootMobile]}>
			{isDesktop ? (
				// The desktop redesign's dock (Figma "Bottom Dock", 1625:4590). The jackpot is no
				// longer pinned above it there: the desktop home draws it in its own right column,
				// and every other screen places it in its content if it has one.
				<DesktopDock
					activeItem={activeFooterItem}
					onBonusPress={handleBonusGamesPress}
					onHomePress={handleHomePress}
					onMissionsPress={handleMissionsPress}
					onRankingPress={handleRankingPress}
					onStorePress={handleStorePress}
					ready={ready}
				/>
			) : isMobile ? (
				<View style={styles.mobileFooterAbsoluteRow}>
					<View style={styles.mobileFooterStack}>
						{showJackpot && <TopJackpotBanner />}
						<View style={styles.mobileFooterContainer}>
							<FooterNavButtonsV2
								activeItem={activeFooterItem}
								onBonusPress={handleBonusGamesPress}
								onHomePress={handleHomePress}
								onMissionsPress={handleMissionsPress}
								onRankingPress={handleRankingPress}
								onStorePress={handleStorePress}
								ready={ready}
								scaleSize={scaleSize}
								width={width}
							/>
						</View>
						{showTournamentMobileNav && (
							<TournamentMobileNav
								width={width}
								scaleSize={scaleSize}
								onMyTablesPress={handleMyTablesPress}
								onTournamentStatsPress={handleTournamentStatsPress}
								activeTablesLabel={t('T_SITE_FOOTER_ACTIVE_TABLES')}
								tournamentsLabel={t('T_SITE_RANKING_TOURNAMENTS')}
							/>
						)}
					</View>
				</View>
			) : (
				<View style={styles.desktopFooterContainer}>
					{isTournaments && (
						<TournamentDesktopBar
							scaleSize={scaleSize}
							tournamentFooterFontSize={tournamentFooterFontSize}
							width={width}
							tournamentFooterSpacer={tournamentFooterSpacer}
							onMyTablesPress={handleMyTablesPress}
							onTournamentStatsPress={handleTournamentStatsPress}
							activeTablesLabel={t('T_SITE_FOOTER_ACTIVE_TABLES')}
							tournamentsLabel={t('T_SITE_RANKING_TOURNAMENTS')}
						/>
					)}
					{showJackpot && <TopJackpotBanner />}
					{/* The nav bar has a single design (Figma nav-base, 371x87). The old desktop
					    variant spread its items across the full width while the plate kept its own
					    shape, so the outer items ended up standing off the plate. */}
					<FooterNavButtonsV2
						activeItem={activeFooterItem}
						onBonusPress={handleBonusGamesPress}
						onHomePress={handleHomePress}
						onMissionsPress={handleMissionsPress}
						onRankingPress={handleRankingPress}
						onStorePress={handleStorePress}
						ready={ready}
						scaleSize={scaleSize}
						width={width}
					/>
				</View>
			)}

			{isBonusGamesModalOpen && <BonusGamesModal onClose={() => setIsBonusGamesModalOpen(false)} />}

			<TournamentStatsModal
				visible={isTournamentStatsOpen}
				onClose={() => setIsTournamentStatsOpen(false)}
			/>
		</View>
	);
};

const createStyles = (
	scaleSize: (value: number) => number,
	width: number,
	mobileFooterHeight: number,
	navPlateWidth: number,
) =>
	StyleSheet.create({
		bottomRoot: {
			width: '100%',
			alignItems: 'center',
		},
		bottomRootMobile: {
			height: mobileFooterHeight,
		},
		mobileFooterAbsoluteRow: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			width: '100%',
			paddingHorizontal: 0,
			position: 'absolute',
			left: 0,
			right: 0,
			bottom: 0,
			zIndex: 1,
			elevation: 1,
		},
		mobileFooterContainer: {
			width,
			alignItems: 'center',
			zIndex: 10,
			elevation: 10,
		},
		mobileFooterStack: {
			position: 'absolute',
			left: 0,
			right: 0,
			// The design leaves 12 of its 390-wide frame under the nav bar.
			bottom: scaleSize(width * (12 / 390)),
			alignItems: 'center',
			zIndex: 10,
			elevation: 10,
		},
		desktopFooterContainer: {
			width,
			alignItems: 'center',
			position: 'relative',
			zIndex: 10,
			// The nav plate ends 1 unit above its own box, so without this it sits flush on the
			// viewport bottom here. The mobile stack gets the same clearance from its own
			// `bottom` offset - the design leaves 12 under the bar - and this branch scales it
			// off however wide the plate ended up.
			paddingBottom: navPlateWidth * (12 / 371),
		},
	});

export default AppFooter;
